import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, AlertCircle, Trash2, Shield, Clock, Database } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import { PAGE_SEO_CONFIG } from '@/utils/seoUtils';

const DeleteAccount = () => {
  const [formData, setFormData] = useState({
    email: '',
    reason: '',
    confirmation: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.confirmation.toLowerCase() !== 'delete my account') {
      setSubmitStatus('error');
      setErrorMessage('Please type "DELETE MY ACCOUNT" to confirm.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://dineai.ai'}/api/account-deletion-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          reason: formData.reason,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ email: '', reason: '', confirmation: '' });
      } else {
        const data = await response.json();
        setSubmitStatus('error');
        setErrorMessage(data.message || 'Failed to submit request. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEOHead
        title={PAGE_SEO_CONFIG.deleteAccount.title}
        description={PAGE_SEO_CONFIG.deleteAccount.description}
        keywords={PAGE_SEO_CONFIG.deleteAccount.keywords?.join(', ')}
      />
      {/* Header */}
      <div className="bg-dineai-blue text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Trash2 className="w-8 h-8" />
            <h1 className="text-3xl md:text-4xl font-bold">Account Deletion Request</h1>
          </div>
          <p className="text-white/80 max-w-2xl">
            We're sorry to see you go. Submit a request to delete your Dine AI account and associated data.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* What Gets Deleted */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-6 h-6 text-red-600" />
                <h2 className="text-xl font-semibold">Data That Will Be Deleted</h2>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>Your account profile and settings</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>Menu data uploaded by you</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>Customer preferences and favorites</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>AI-generated images and content</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>Analytics and usage data</span>
                </li>
              </ul>
            </div>

            {/* What Gets Retained */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold">Data Retained (Legal Requirements)</h2>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Transaction records (tax/accounting laws)</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Payment history (regulatory compliance)</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Basic audit logs (security purposes)</span>
                </li>
              </ul>
              <p className="text-sm text-gray-500 mt-4 italic">
                This data is anonymized and retained for 7 years as required by law.
              </p>
            </div>
          </div>

          {/* Processing Timeline */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-3">
              <Clock className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-yellow-900 mb-2">Processing Timeline</h3>
                <p className="text-yellow-800">
                  Account deletion requests are processed within <strong>30 days</strong>. You will receive a confirmation email
                  when your request is received and another when deletion is complete.
                </p>
              </div>
            </div>
          </div>

          {/* Deletion Request Form */}
          <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-200">
            <h2 className="text-2xl font-bold mb-6">Submit Deletion Request</h2>

            {submitStatus === 'success' && (
              <Alert className="mb-6 bg-green-50 border-green-200">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <AlertDescription className="text-green-800">
                  <strong>Request submitted successfully!</strong> You will receive a confirmation email shortly.
                  Your account will be deleted within 30 days.
                </AlertDescription>
              </Alert>
            )}

            {submitStatus === 'error' && (
              <Alert className="mb-6 bg-red-50 border-red-200">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <AlertDescription className="text-red-800">
                  <strong>Error:</strong> {errorMessage}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-600">*</span>
                </label>
                <Input
                  id="email"
                  type="email"
                  required
                  placeholder="Enter the email associated with your account"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Use the email you signed up with (Google, Facebook, Twitter, or Apple account)
                </p>
              </div>

              {/* Reason */}
              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Deletion (Optional)
                </label>
                <Textarea
                  id="reason"
                  placeholder="Help us improve by sharing why you're leaving (optional)"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full min-h-24"
                />
              </div>

              {/* Confirmation */}
              <div>
                <label htmlFor="confirmation" className="block text-sm font-medium text-gray-700 mb-2">
                  Type "DELETE MY ACCOUNT" to confirm <span className="text-red-600">*</span>
                </label>
                <Input
                  id="confirmation"
                  type="text"
                  required
                  placeholder="DELETE MY ACCOUNT"
                  value={formData.confirmation}
                  onChange={(e) => setFormData({ ...formData, confirmation: e.target.value })}
                  className="w-full"
                />
                <p className="text-sm text-red-600 mt-1">
                  This action cannot be undone. All your data will be permanently deleted.
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-red-600 hover:bg-red-700 text-white px-8"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Deletion Request'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.history.back()}
                  className="px-8"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>

          {/* Support */}
          <div className="mt-8 text-center text-gray-600">
            <p>
              Need help or have questions? Contact us at{' '}
              <a href="mailto:support@dineai.ai" className="text-dineai-blue hover:underline font-medium">
                support@dineai.ai
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
